import RichTextEditor from "./ck-editor-text";

interface Props {
    isOpen: boolean;
    title?: string;
    value: string;
    onChange: (value: string) => void;
    onClose?: () => void;
    onSave?: () => void;
    isLoading?: boolean;
}

export default function CKEditor({ isLoading = false, value, onChange, onClose, onSave, title = "", isOpen }: Props) {
    if (!isOpen) return null;

    const formatTitle = (value: string) => value ? value.charAt(0).toUpperCase() + value.slice(1) : "Content";
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="my-auto flex h-[96dvh] w-full min-h-0 flex-col overflow-hidden rounded bg-[#f5f9fd] shadow sm:h-[90dvh] sm:max-w-[95vw] lg:max-w-[700px] xl:max-w-[1000px] 2xl:max-w-[1100px]">
                
                {/* Modal Header */}
                <div className="flex shrink-0 items-center justify-between border-b bg-white px-6 py-5 shadow">
                    <div className="min-w-0">
                        <h2 className="text-sm font-medium">
                            {`Edit content${title ? ` - ${title}` : ""}`}
                        </h2>

                        <p className="text-xs text-gray-500">
                            Edit and format the content below.
                        </p>
                    </div>

                    <button
                        type="button"
                        disabled={isLoading}
                        onClick={onClose}
                        className="ml-4 flex h-9 w-9 shrink-0 items-center justify-center rounded border text-lg hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                {/* Modal Body */}
                <div className="min-h-0 flex-1 p-4 sm:px-5 sm:py-4">
                    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                        
                        {/* Editor Card Header */}
                        <div className="flex shrink-0 items-center justify-between border-b border-[#186cb2]/15 bg-[#eef6fc] px-4 py-3">
                            <div className="min-w-0">
                                <h2 className="text-sm font-medium text-[#186cb2]">
                                    {formatTitle(title)}
                                </h2>

                                <p className="mt-0.5 text-xs text-gray-500">
                                    Edit and format the content as needed.
                                </p>
                            </div>
                        </div>

                        {/* Editor */}
                        <div className="min-h-0 flex-1 p-4">
                            <RichTextEditor
                                disabled={isLoading}
                                value={value}
                                onChange={onChange}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex shrink-0 items-center justify-end gap-2 border-t bg-white px-4 py-3 sm:px-5 sm:py-4">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className="rounded border px-4 py-2 text-xs font-medium hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={isLoading}
                        onClick={onSave}
                        className="flex items-center justify-center gap-2 rounded bg-[#186cb2] px-4 py-2 text-xs font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                        {isLoading && (
                            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        )}

                        {isLoading ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}